import { platformService } from './platformIntegrations';
import { supabase } from '../supabase';

class GoogleCalendarService {

  async _fetchCalendarEvents(accessToken, timeMin, timeMax) {
    // API reference: https://developers.google.com/calendar/api/v3/reference/events/list
    const url = new URL('https://www.googleapis.com/calendar/v3/calendars/primary/events');
    url.searchParams.append('singleEvents', 'true');
    url.searchParams.append('orderBy', 'startTime');
    if (timeMin) url.searchParams.append('timeMin', timeMin);
    if (timeMax) url.searchParams.append('timeMax', timeMax);

    return await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
  }

  async listEvents(workspaceId, timeMin, timeMax) {
    try {
      // 1. Get credentials securely
      let credentials = await platformService.getCredentials(workspaceId, 'google_calendar');

      // Fallback to master google credentials
      if (!credentials || !credentials.accessToken) {
        credentials = await platformService.getCredentials(workspaceId, 'google');
      }

      if (!credentials || !credentials.accessToken) {
        throw new Error('Google Calendar not connected or missing access token');
      }

      let accessToken = credentials.accessToken;
      let response = await this._fetchCalendarEvents(accessToken, timeMin, timeMax);

      // Handle Token Expiry
      if (response.status === 401 || response.status === 403) {
        console.log("Token expired, attempting refresh...");

        // Attempt Refresh via Edge Function
        // Using 'google' platform identifier if that's what has the refresh token, or 'google_calendar'
        const platformToRefresh = credentials.provider === 'google' ? 'google' : 'google_calendar';

        const { data, error } = await supabase.functions.invoke('refresh-token', {
          body: { workspaceId, platform: platformToRefresh }
        });

        if (error || !data || !data.success) {
          console.error("Refresh failed:", error || data?.error);
          throw new Error('Access token expired and refresh failed');
        }

        // Retry with new token
        accessToken = data.accessToken;
        response = await this._fetchCalendarEvents(accessToken, timeMin, timeMax);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to fetch events from Google Calendar');
      }

      const data = await response.json();

      // Map events to the standard format used by MarketingCalendar
      return (data.items || []).map(event => ({
        id: `gcal-${event.id}`,
        title: event.summary || 'Untitled Event',
        description: event.description || '',
        type: 'google_calendar',
        // event.start.dateTime is for timed events, event.start.date is for all-day events
        event_date: event.start?.dateTime || event.start?.date,
        end_date: event.end?.dateTime || event.end?.date,
        location: event.location || '',
        source: 'google_calendar'
      }));

    } catch (error) {
      console.error('Google Calendar API Error:', error);
      throw error;
    }
  }
}

export const googleCalendarService = new GoogleCalendarService();
